import {
  WORKSPACE_TABLE_BOARD_LIMIT,
  DUE_BUCKETS,
  KEYWORD_MAX_LENGTH,
  NO_FACET_MATCH,
  workspaceTableQuerySchema,
  workspaceTableSchema,
  ulidSchema,
  type ApiError,
  type DueBucket,
  type WorkspaceTable,
  type WorkspaceTableRow,
} from '@molt/shared';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { get } from './api';
import { queryKeys } from './query-keys';
import {
  nextTableSort,
  type BoardTableSort,
  type BoardTableSortKey,
} from './board-table';

export { WORKSPACE_TABLE_BOARD_LIMIT, nextTableSort };
export type { BoardTableSort, BoardTableSortKey };

export interface WorkspaceTableRequest {
  readonly workspaceId: string;
  readonly boardIds: readonly string[];
  readonly filter?: WorkspaceTableFilterSelection | undefined;
}

export interface WorkspaceTableFilterSelection {
  readonly members: readonly string[];
  readonly labels: readonly string[];
  readonly lists: readonly string[];
  readonly due: readonly DueBucket[];
  readonly keyword: string;
}

export const EMPTY_WORKSPACE_TABLE_FILTER: WorkspaceTableFilterSelection = {
  members: [],
  labels: [],
  lists: [],
  due: [],
  keyword: '',
};

export function workspaceTableQueryString(
  request: Omit<WorkspaceTableRequest, 'workspaceId'>,
): string {
  const filter = normaliseWorkspaceTableFilter(
    request.filter ?? EMPTY_WORKSPACE_TABLE_FILTER,
  );
  const parsed = workspaceTableQuerySchema.parse({
    boardIds: request.boardIds,
    ...(filter.members.length === 0 ? {} : { members: filter.members }),
    ...(filter.labels.length === 0 ? {} : { labels: filter.labels }),
    ...(filter.lists.length === 0 ? {} : { lists: filter.lists }),
    ...(filter.due.length === 0 ? {} : { due: filter.due }),
    ...(filter.keyword === '' ? {} : { keyword: filter.keyword }),
  });
  const params = new URLSearchParams();
  for (const boardId of parsed.boardIds) params.append('boardIds', boardId);
  for (const member of parsed.members ?? []) params.append('members', member);
  for (const label of parsed.labels ?? []) params.append('labels', label);
  for (const list of parsed.lists ?? []) params.append('lists', list);
  for (const due of parsed.due ?? []) params.append('due', due);
  if (parsed.keyword !== undefined) params.set('keyword', parsed.keyword);
  return params.toString();
}

export function getWorkspaceTable(
  request: WorkspaceTableRequest,
): Promise<WorkspaceTable> {
  return get(
    `/workspaces/${encodeURIComponent(request.workspaceId)}/table?${workspaceTableQueryString(request)}`,
    workspaceTableSchema,
  );
}

export function workspaceTableQueryOptions(
  request: WorkspaceTableRequest,
): UseQueryOptions<WorkspaceTable, ApiError> {
  const queryKey = workspaceTableQueryString(request);
  return {
    queryKey: queryKeys.workspaces.table(request.workspaceId, queryKey),
    queryFn: () => getWorkspaceTable(request),
  };
}

export function useWorkspaceTable(
  request: WorkspaceTableRequest,
  enabled: boolean,
): UseQueryResult<WorkspaceTable, ApiError> {
  return useQuery({ ...workspaceTableQueryOptions(request), enabled });
}

export function selectedWorkspaceTableBoardIdsFromSearch(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : value === undefined ? [] : [value];
  const selected: string[] = [];
  const seen = new Set<string>();

  for (const item of raw) {
    if (typeof item !== 'string' || item.trim() === '') continue;
    if (!ulidSchema.safeParse(item).success) continue;
    if (seen.has(item)) continue;
    seen.add(item);
    selected.push(item);
    if (selected.length === WORKSPACE_TABLE_BOARD_LIMIT) break;
  }

  return selected;
}

export function cappedWorkspaceTableBoardSelection(ids: readonly string[]): string[] {
  return ids.slice(0, WORKSPACE_TABLE_BOARD_LIMIT);
}

export function workspaceTableFilterFromSearch(
  search: string | URLSearchParams | Record<string, unknown>,
): WorkspaceTableFilterSelection {
  const params =
    search instanceof URLSearchParams || typeof search === 'string'
      ? new URLSearchParams(search)
      : searchParamsFromRecord(search);

  return normaliseWorkspaceTableFilter({
    members: params.getAll('members'),
    labels: params.getAll('labels'),
    lists: params.getAll('lists'),
    due: params.getAll('due') as DueBucket[],
    keyword: params.get('keyword') ?? '',
  });
}

export function workspaceTableFilterSearchParams(
  filter: WorkspaceTableFilterSelection,
): URLSearchParams {
  const params = new URLSearchParams();
  const normalised = normaliseWorkspaceTableFilter(filter);
  for (const member of normalised.members) params.append('members', member);
  for (const label of normalised.labels) params.append('labels', label);
  for (const list of normalised.lists) params.append('lists', list);
  for (const due of normalised.due) params.append('due', due);
  if (normalised.keyword !== '') params.set('keyword', normalised.keyword);
  return params;
}

export function toggleWorkspaceTableFilterValue(
  filter: WorkspaceTableFilterSelection,
  facet: 'members' | 'labels' | 'lists' | 'due',
  value: string,
): WorkspaceTableFilterSelection {
  const current = new Set<string>(filter[facet]);
  if (current.has(value)) {
    current.delete(value);
  } else {
    current.add(value);
  }
  return normaliseWorkspaceTableFilter({ ...filter, [facet]: [...current] });
}

export function setWorkspaceTableKeyword(
  filter: WorkspaceTableFilterSelection,
  keyword: string,
): WorkspaceTableFilterSelection {
  return normaliseWorkspaceTableFilter({ ...filter, keyword });
}

export function sortedWorkspaceTableRows(
  rows: readonly WorkspaceTableRow[],
  sort: BoardTableSort | null,
): WorkspaceTableRow[] {
  const copy = [...rows];
  if (sort === null) return copy;
  const direction = sort.direction === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    const compared = compareWorkspaceRows(a, b, sort.key);
    if (compared !== 0) return compared * direction;
    return (
      compareText(a.board.title, b.board.title) ||
      compareText(a.list.title, b.list.title) ||
      compareText(a.card.title, b.card.title)
    );
  });
}

function compareWorkspaceRows(
  a: WorkspaceTableRow,
  b: WorkspaceTableRow,
  key: BoardTableSortKey,
): number {
  if (key === 'card') return compareText(a.card.title, b.card.title);
  if (key === 'list') return compareText(a.list.title, b.list.title);
  if (a.card.dueAt === null && b.card.dueAt === null) return 0;
  if (a.card.dueAt === null) return 1;
  if (b.card.dueAt === null) return -1;
  return new Date(a.card.dueAt).getTime() - new Date(b.card.dueAt).getTime();
}

function normaliseWorkspaceTableFilter(
  filter: WorkspaceTableFilterSelection,
): WorkspaceTableFilterSelection {
  return {
    members: normaliseFacet(filter.members, true),
    labels: normaliseFacet(filter.labels, true),
    lists: normaliseFacet(filter.lists, false),
    due: normalise(filter.due).filter(isDueBucket),
    keyword: normaliseKeyword(filter.keyword),
  };
}

function normaliseFacet(values: readonly string[], allowNone: boolean): string[] {
  return normalise(values).filter(
    (value) => value.length === 26 || (allowNone && value === NO_FACET_MATCH),
  );
}

function normalise(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value !== ''))].sort();
}

function isDueBucket(value: string): value is DueBucket {
  return DUE_BUCKETS.includes(value as DueBucket);
}

function normaliseKeyword(value: string): string {
  return value.trim().slice(0, KEYWORD_MAX_LENGTH);
}

function searchParamsFromRecord(search: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    for (const item of arraySearchValue(value)) params.append(key, item);
  }
  return params;
}

function arraySearchValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  return typeof value === 'string' ? [value] : [];
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}
